import { ReactNode, Suspense } from "react";
import * as Sentry from "@sentry/react";
import { Card, CardContent, CardHeader } from "@/components/ui/card";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/catalyst/button";
import { ArrowPathIcon } from "@heroicons/react/16/solid";
import { ExclamationTriangleIcon } from "@heroicons/react/24/outline";
import { useTranslations } from "use-intl";

function BlockSkeleton() {
  return (
    <Card className="flex flex-col">
      <CardHeader>
        <Skeleton className="h-6 w-40" />
      </CardHeader>
      <CardContent className="flex flex-1 flex-col">
        <Skeleton className="aspect-video w-full flex-1" />
      </CardContent>
    </Card>
  );
}

function BlockErrorFallback({ resetError }: { resetError: () => void }) {
  const t = useTranslations("dashboard");

  return (
    <Card className="flex flex-col">
      <CardContent className="flex flex-1 flex-col items-center justify-center gap-3 py-12 text-center">
        <ExclamationTriangleIcon className="h-8 w-8 text-gray-400" />
        <p className="text-sm text-gray-500">{t("blockBoundary.error")}</p>
        <Button outline onClick={resetError} LeadingIcon={ArrowPathIcon}>
          {t("blockBoundary.retry")}
        </Button>
      </CardContent>
    </Card>
  );
}

export function DashboardBlockBoundary({ children }: { children: ReactNode }) {
  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <BlockErrorFallback resetError={resetError} />
      )}
    >
      <Suspense fallback={<BlockSkeleton />}>{children}</Suspense>
    </Sentry.ErrorBoundary>
  );
}
